import React, { useRef, useCallback, useMemo, useState } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { withStyles } from '~/themes/useStyles'
import style from './style.js'
import GalleryDialog from '~/components/common/Gallery'
import useGalleryDialog from '~/utils/hooks/useGalleryDialog'
import { MEDIA_FILE_TYPE } from '~/constants/media'
import './CustomVideo'
import Switch from './CustomSwitch.js'

const TextEditor = ({
  classes,
  value,
  onChange,
  removeMedia,
  isProductMode,
  containerToolbar,
  noUploadVideo,
  disabled,
  className,
  error,
  disableFirstChangeCondition,
  switchType,
}) => {
  const editorRef = useRef()
  const wrapperRef = useRef()
  const [activeGallery, setActiveGallery] = useState(false)
  const [firstChange, setFirstChange] = useState(true)
  const [images, setImages] = useState([])
  const [galleryProps, galleryDispatch] = useGalleryDialog()
  const [mediaSelectType, setMediaSelectType] = useState([
    MEDIA_FILE_TYPE.IMAGE,
  ])
  const [mode, setMode] = useState(true)
  const [textareaHeight, setTextareaHeight] = useState('auto')

  const onHandleVideo = useCallback(() => {
    if (disabled) return
    setActiveGallery(true)
    setImages([])
    setMediaSelectType([MEDIA_FILE_TYPE.VIDEO])
    galleryDispatch.openGallery()
  }, [setActiveGallery, galleryDispatch, setImages, disabled])

  const onHandleImage = useCallback(() => {
    if (disabled) return
    setActiveGallery(true)
    setImages([])
    setMediaSelectType([MEDIA_FILE_TYPE.IMAGE])
    galleryDispatch.openGallery()
  }, [setActiveGallery, galleryDispatch, setImages, disabled])

  const insertMedia = useCallback(
    (files) => {
      galleryDispatch.closeGallery()
      setImages(files)
      if (!editorRef.current) return
      if (!files || files.length === 0) return
      const filesLength = files.length
      for (let i = 0; i < filesLength; i += 1) {
        const file = files[i]
        const fileType =
          file && file.contentType && file.contentType.split('/')[0]
        const selectionIndex =
          editorRef.current.getEditor().getSelection(true)?.index || 0
        if (fileType === 'image') {
          if (file && file.src) {
            editorRef.current
              .getEditor()
              .insertEmbed(selectionIndex, 'image', file.src)
            editorRef.current.getEditor().setSelection(selectionIndex + 1)
          }
        } else if (fileType === 'video') {
          if (file && file.src) {
            editorRef.current
              .getEditor()
              .insertEmbed(selectionIndex, 'video', file.src, 'user')
            editorRef.current.getEditor().format('width', 300)
            editorRef.current.getEditor().format('height', 300)
            editorRef.current.getEditor().setSelection(selectionIndex + 1)
          }
        }
      }
      editorRef.current.focus()
    },
    [galleryDispatch, setImages],
  )

  const modules = useMemo(
    () => ({
      toolbar: {
        container: containerToolbar.length
          ? containerToolbar
          : [
              [{ size: [] }],
              [{ font: [] }],
              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
              [
                { list: 'ordered' },
                { list: 'bullet' },
                { indent: '-1' },
                { indent: '+1' },
              ],
              ['link', 'image', 'video'],
              ['clean'],
            ],
        handlers: {
          image: onHandleImage,
          video: onHandleVideo,
        },
      },
      clipboard: { matchVisual: false },
    }),
    [onHandleImage, onHandleVideo],
  )

  const htmlToText = (html) => {
    let text = html
    // text = text.replace(/\n/gi, '')
    text = text.replace(/<style([\s\S]*?)<\/style>/gi, '')
    text = text.replace(/<script([\s\S]*?)<\/script>/gi, '')
    text = text.replace(/<a.*?href="(.*?)[?"].*?>(.*?)<\/a.*?>/gi, ' $2 $1 ')
    text = text.replace(/<p>/gi, '')
    text = text.replace(/<\/p>/gi, '\n')
    text = text.replace(/<ul>/gi, '<ul>\n')
    text = text.replace(/<\/ul>/gi, '</ul>\n')
    text = text.replace(/<ol>/gi, '<ol>\n')
    text = text.replace(/<\/ol>/gi, '</ol>\n')
    text = text.replace(/<\/div>/gi, '\n\n')
    text = text.replace(/<li>/gi, '<li>')
    text = text.replace(/<\/li>/gi, '\n')
    text = text.replace(/<br\s*\/?>/gi, '') // Replace line break tags with a newline character
    // text = text.replace(/<[^>]+>/gi, '');  // Remove all other HTML tags
    // text = text.replace(/^\s*/gim, '') // Remove leading whitespace on each line
    // text = text.replace(/ ,/gi, ',')
    // text = text.replace(/ +/gi, ' ');  // Replace multiple spaces with a single space
    // text = text.replace(/\n+/gi, '\n\n');  // Replace multiple consecutive newlines with two newlines
    return text
  }

  const countLines = (text) => {
    const lines = text.split('\n')
    return lines.length
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {switchType ? (
        <div className={classes.switchContainer}>
          <Switch
            checked={mode}
            disabled={disabled}
            onChange={() => {
              if (mode) {
                setTextareaHeight(
                  `${wrapperRef?.current?.offsetHeight ? wrapperRef?.current?.offsetHeight - 49 : 0}px`,
                )
              }
              setMode(!mode)
            }}
          />
        </div>
      ) : null}
      {mode ? (
        <ReactQuill
          ref={editorRef}
          className={clsx(
            classes.quillContainer,
            disabled ? classes.disabled : '',
            {
              [className]: !!className,
            },
            { [classes.error]: error },
          )}
          theme="snow"
          value={value}
          onChange={(v, delta, source) => {
            const { ops } = delta
            const isUploadImg = ops.some((op) => !!op?.insert?.image)
            if (disableFirstChangeCondition) {
              onChange(`${v}<p><br></p>`)
            } else {
              setFirstChange(false)
              /*
                only update onChange when source is "user"
                upload image source is always "api"
                because React Quill uses a built-in mechanism to store uploaded images on a server through an API
              */
              if (
                (!firstChange && (source === 'user' || isUploadImg)) ||
                (firstChange && source === 'user')
              ) {
                onChange(`${v}<p><br></p>`)
              }
            }
          }}
          modules={modules}
          formats={ReactQuill.formats}
          bounds=".container"
          preserveWhitespace
          matchVisual
          readOnly={disabled}
        />
      ) : (
        <textarea
          style={{ overflow: 'auto', height: textareaHeight }}
          className={classes.textArea}
          value={htmlToText(value)}
          onChange={(e) => {
            const lineCount = countLines(e.target.value)
            if (lineCount > 7) {
              setTextareaHeight('auto')
              setTextareaHeight(`${(lineCount - 1) * 20}px`)
            }
            onChange(e.target.value)
          }}
        />
      )}
      {activeGallery && (
        <GalleryDialog
          images={images}
          {...galleryProps}
          multiple
          handleSave={insertMedia}
          showTypes={mediaSelectType}
          selectTypes={mediaSelectType}
          noUploadVideo={noUploadVideo}
          isProductMode={isProductMode}
          removeMedia={removeMedia}
          acceptTypes={
            mediaSelectType.indexOf('image') !== -1
              ? '.jpg,.png,.jpeg,.webp,.svg'
              : mediaSelectType.indexOf('video') !== -1
                ? '.mp4,.mov,.webm'
                : '.jpg,.png,.jpeg,.mp4,.mov,.webp,.svg,.webm'
          }
          enabledEditMetadataIds={['alternative_text', 'title']}
        />
      )}
    </div>
  )
}

TextEditor.defaultProps = {
  value: '',
  className: '',
  // onHandleVideo: () => {},
  onChange: () => {},
  props: {},
  removeMedia: false,
  isProductMode: true,
  containerToolbar: [],
  noUploadVideo: false,
  disabled: false,
  error: false,
  disableFirstChangeCondition: false,
  switchType: true,
  classes: null,
}

TextEditor.propTypes = {
  classes: PropTypes.shape(),
  value: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func,
  // onHandleVideo: PropTypes.func,
  props: PropTypes.shape(),
  isProductMode: PropTypes.bool,
  removeMedia: PropTypes.bool,
  containerToolbar: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape(),
      PropTypes.arrayOf(PropTypes.shape()),
    ]),
  ),
  noUploadVideo: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  disableFirstChangeCondition: PropTypes.bool,
  switchType: PropTypes.bool,
}

export default withStyles(style)(TextEditor)
